<!--
@component
MatchRail

Shared visual rail used by both `AllMatches` and `TeamMatches`. Owns
the grouping, horizontal scroll, scroll buttons and the trailing CTA.
The caller only needs to compute the ordered list of matches to display
and optionally pass a `team` so the CTA points at that team's page
instead of the full schedule.
-->

<script lang="ts">
  import Match from '$lib/components/common/MatchesWidget/Match.svelte';
  import { homepage } from '$pkg';
  import type { ContestedMatch } from '$lib/@types/opta';
  import getTeamPath from '$utils/getTeamPath';
  import { useHorizontalScroll } from './useHorizontalScroll.svelte';

  const MATCH_WIDTH_PX = 175;

  /**
   * Minimal shape needed to build a team-page link. Matches the team
   * objects returned by `$utils/getTeams`.
   */
  type RailTeam = {
    name?: string;
    slug?: string;
    path?: string;
  };

  type Props = {
    /** Ordered list of matches to render in the rail. */
    matches: ContestedMatch[];
    /**
     * Optional team. When provided, the trailing CTA links to that
     * team's page and reads "View all matches"; otherwise it links
     * to the full schedule and reads "View full schedule".
     *
     * The URL is resolved through `getTeamPath`, which already
     * respects the global `appState.embed` flag — so embed callers
     * automatically get an absolute production URL.
     */
    team?: RailTeam | null;
    /**
     * Optional match `id` to horizontally scroll into view on mount.
     * Used by `TeamMatches` to anchor the rail on the team's next
     * upcoming (or live) match so users land on it by default,
     * rather than at the start of the played-matches tail.
     *
     * Has no effect when the match id isn't present in `matches`,
     * or when the rail doesn't overflow.
     */
    focusMatchId?: string | null;
  };

  const { matches, team = null, focusMatchId = null }: Props = $props();

  /** Pull the name of the round a match belongs to. */
  const getRoundName = (match: {
    matchInfo?: {
      series?: { name?: string };
      stage?: { name?: string };
    };
  }): string =>
    match?.matchInfo?.series?.name ?? match?.matchInfo?.stage?.name ?? '';

  /** Bucket consecutive matches sharing a round name into one card. */
  type MatchGroup = { roundName: string; matches: ContestedMatch[] };
  const matchGroups = $derived<MatchGroup[]>(
    matches.reduce<MatchGroup[]>((acc, match) => {
      const roundName = getRoundName(match);
      const last = acc[acc.length - 1];
      if (last && last.roundName === roundName) {
        last.matches.push(match);
      } else {
        acc.push({ roundName, matches: [match] });
      }
      return acc;
    }, [])
  );

  const scheduleHref = $derived(`${homepage.replace(/\/$/, '')}/schedule/`);

  /**
   * Resolve the trailing CTA's href + label. When a `team` prop is
   * passed (the team-page variant), defer to `getTeamPath` for the
   * URL — it already produces an absolute production URL when
   * `appState.embed` is set. Otherwise fall back to the full
   * schedule.
   */
  const cta = $derived.by(() => {
    if (team) {
      const teamPath = getTeamPath(team);
      if (teamPath?.url) {
        return { href: teamPath.url, label: 'View all matches' };
      }
    }
    return { href: scheduleHref, label: 'View full schedule' };
  });

  const keyOf = (m: ContestedMatch, i: number) => m?.matchInfo?.id ?? i;

  const scroll = useHorizontalScroll({
    scrollFraction: 0.8,
    scrollCheckDelay: 300,
    // Small threshold so the left/right buttons remain visible until
    // the rail is essentially at the very edge. Earlier we had to use
    // a much larger value (56) to compensate for `scroll-padding-left`
    // which has since been removed.
    edgeThreshold: 4,
  });

  $effect(() => {
    return scroll.setupScrollMonitoring();
  });

  // Re-check overflow when the match list changes.
  $effect(() => {
    matches.length;
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => scroll.checkOverflow());
    }
  });

  /**
   * On mount (and any time `focusMatchId` / the matches list change),
   * scroll the matching `[data-match-id]` cell into view so the rail
   * lands on the team's next match instead of at the start. We jump
   * instantly (no smooth-scroll) so the initial render doesn't
   * animate, and clamp to the rail's max scroll so we never overshoot
   * the end.
   */
  $effect(() => {
    if (!focusMatchId) return;
    matches.length; // re-run when the list changes
    if (typeof requestAnimationFrame === 'undefined') return;

    const raf = requestAnimationFrame(() => {
      const container = scroll.contentElement;
      if (!container) return;

      const cell = container.querySelector<HTMLElement>(
        `[data-match-id="${focusMatchId}"]`
      );
      if (!cell) return;

      // Compute the target scroll position relative to the rail's
      // leading edge, accounting for the container's own `padding-
      // inline` so the cell lines up nicely with the left gutter.
      const containerRect = container.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();
      const padLeft =
        parseFloat(getComputedStyle(container).paddingLeft || '0') || 0;
      const target =
        cellRect.left - containerRect.left + container.scrollLeft - padLeft;

      const maxScroll = container.scrollWidth - container.clientWidth;
      container.scrollTo({
        left: Math.max(0, Math.min(target, maxScroll)),
        behavior: 'instant' as ScrollBehavior,
      });

      scroll.checkOverflow();
    });

    return () => cancelAnimationFrame(raf);
  });
</script>

<div class="widget-content" class:has-overflow={scroll.hasOverflow}>
  <div
    class="cards-container"
    class:has-overflow={scroll.hasOverflow}
    style="--matchCount:{matches.length}; --match-width:{MATCH_WIDTH_PX}px;"
    bind:this={scroll.contentElement}
  >
    <div
      class="rail"
      style="--matchCount:{matches.length}; --match-width:{MATCH_WIDTH_PX}px;"
    >
      {#each matchGroups as group, gi (group.roundName + '-' + gi)}
        <div class="match-group" style="--groupCount:{group.matches.length};">
          <div class="round-label pb-1 pl-2 text-xxs text-secondary">
            {group.roundName}
          </div>
          <div class="match-strip" class:multi={group.matches.length > 1}>
            {#each group.matches as match, i (keyOf(match, i))}
              <div
                class="match-cell"
                data-match-id={match?.matchInfo?.id ?? ''}
              >
                <Match {match} />
              </div>
            {/each}
          </div>
        </div>
      {/each}
      <!-- <a target="_top" href={cta.href} class="view-all-button button-primary">
        {cta.label}
      </a> -->
      <div class="rail-end-spacer" aria-hidden="true"></div>
    </div>
  </div>

  {#if scroll.hasOverflow && !scroll.isAtStart}
    <button
      type="button"
      class="scroll-button left"
      onclick={scroll.scrollLeft}
      aria-label="Scroll left"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>
  {/if}

  {#if scroll.hasOverflow && !scroll.isAtEnd}
    <button
      type="button"
      class="scroll-button right"
      onclick={scroll.scrollRight}
      aria-label="Scroll right"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </button>
  {/if}
</div>

<style lang="scss">
  @use 'horizontalScroll';
  @use '../../../styles/mixins' as mixins;

  a {
    text-decoration: none !important;
  }

  .widget-content {
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    overflow-x: clip;
    overflow-y: visible;
  }

  .cards-container {
    @extend .horizontal-scroll-container;
    width: 100%;
    // Vertical padding gives the hover drop-shadow room to breathe
    // on the top/bottom; inline padding does the same horizontally
    // for the first and last cards. Without this padding the shadow
    // on edge cards gets clipped by the scroll container's
    // `overflow-x: auto`.
    padding-block: 0.5rem 1rem;
    padding-inline: 0.5rem;
    scroll-behavior: smooth;
    // Proximity snap (not mandatory) so user-driven scrolls land
    // tidily on a card without forcing the rail past edge positions.
    // No `scroll-padding-*` here — earlier that offset the snap
    // points and left the rail stuck partway when scrolling back to
    // the start. The JS scroll buttons explicitly clamp to 0 / max
    // so reaching the very edges still works.
    scroll-snap-type: x proximity;

    @media (prefers-reduced-motion: reduce) {
      scroll-behavior: auto;
      scroll-snap-type: none;
    }
  }

  .rail {
    display: flex;
    flex-wrap: nowrap;
    // Align rail children to the bottom so the trailing
    // "View full schedule" button lines up with the match cards
    // regardless of the round-label height above each match group.
    align-items: flex-end;
    gap: 0.5rem;
    width: max-content;
    position: relative;
    z-index: 1;

    margin: 0 auto;
  }

  .rail-end-spacer {
    // No-op: scroll-snap removed; spacer kept as a structural anchor.
  }

  .match-group {
    display: flex;
    flex-direction: column;
    flex: 0 0 calc(var(--groupCount) * var(--match-width, 175px));
    width: calc(var(--groupCount) * var(--match-width, 175px));
    // Snap each match group's leading edge to the rail's leading
    // edge so user scrolls "rest" on a card boundary.
    scroll-snap-align: start;
  }

  .round-label {
    text-transform: none;
    text-align: left;
    line-height: 1.1;
    min-height: var(--theme-font-size-xxs);
  }

  .match-strip {
    background-color: var(--theme-colour-muted-grey);
    border-radius: calc(1 * var(--theme-style-border-radius));
    position: relative;
    overflow: visible;
    display: flex;
    box-shadow: 1px 1px 1px var(--theme-colour-tan-grey);
    flex-direction: row;
    align-items: stretch;
  }

  // Adjacent cells get a vertical divider so a multi-match group
  // reads as one card with separators.
  .match-cell {
    flex: 1 1 0;
    min-width: 0;
    position: relative;

    & + .match-cell {
      border-left: 1px solid var(--theme-colour-tan-grey);
    }
  }

  .match-strip :global(> .match-cell > a) {
    width: 100% !important;
    max-width: none !important;
    display: block;
    border-right: 0 !important;
  }

  // Round corners manually since each anchor is wrapped in a
  // `.match-cell`, defeating the upstream `:first/:last-child` rules.
  .match-strip :global(.match) {
    border-radius: 0 !important;
  }

  .match-strip .match-cell:first-child :global(.match) {
    border-top-left-radius: calc(
      1 * var(--theme-style-border-radius)
    ) !important;
    border-bottom-left-radius: calc(
      1 * var(--theme-style-border-radius)
    ) !important;
  }

  .match-strip .match-cell:last-child :global(.match) {
    border-top-right-radius: calc(
      1 * var(--theme-style-border-radius)
    ) !important;
    border-bottom-right-radius: calc(
      1 * var(--theme-style-border-radius)
    ) !important;
  }

  .match-strip :global(.match:hover) {
    @include mixins.hard-shadow;
  }

  .match-strip :global(.live-container) {
    @include mixins.fpx-2;
    position: absolute !important;
    bottom: -0.65rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
  }

  .view-all-button {
    display: flex;
    flex: 0 0 auto;
    justify-content: center;
    align-items: center;
    width: 100px;
    min-height: 65px;
    background: var(--theme-colour-muted-grey);
    color: var(--theme-colour-logo);
    text-decoration: none;
    text-align: center;
    text-transform: uppercase;
    font-weight: 600;
    border-radius: calc(1 * var(--theme-style-border-radius));
    box-shadow: 1px 1px 1px var(--theme-colour-tan-grey);
    scroll-snap-align: end;
    transition: all 0.2s ease;
    @include mixins.text-xs;
    @include mixins.fpx-3;

    &:hover {
      background: var(--theme-colour-logo);
      color: var(--theme-colour-white);
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
</style>
